import useOnOffState from 'hooks/common/useOnOffState';
import { useRequestCancel } from 'hooks/common/useRequestCancel';

import { updateCampaignStatus } from 'components/CampaignsDashboard/utils/updateCampaignStatus';
import {
    CampaignTableModerationStatus,
    getCampaignTableModerationStatus,
    getIsActiveStatus,
} from 'components/CampaignsDashboard/utils/utils';

import {
    CampaignFrontendStatus,
    DashboardCampaign,
    setBudgetExceededAlert,
    setInactiveDueToEndDateAlert,
    setIsCampaignInsufficientBalanceAlertVisible,
    setModerationFailedAlert,
    setStopCampaignAlert,
} from 'store/models/campaignsDashboard';
import { useDispatch } from 'store/utils';

export const useCampaignStatusToggle = (
    campaign: DashboardCampaign,
    isInsufficientBalance: boolean
): {
    isStatusToggleLoading: boolean;
    onToggleStatus: () => void;
} => {
    const [isStatusToggleLoading, setIsStatusToggleLoadingOn, setIsStatusToggleLoadingOff] = useOnOffState(false);

    const dispatch = useDispatch();
    const { abortRequest, abortSignal } = useRequestCancel();

    const { id: campaignId, frontendStatus, banners, productType } = campaign;
    const isActiveStatus = getIsActiveStatus(frontendStatus);
    const isModerationFailed =
        getCampaignTableModerationStatus(banners, productType, frontendStatus) ===
        CampaignTableModerationStatus.Rejected;

    const onToggleStatus = () => {
        if (isStatusToggleLoading || frontendStatus === CampaignFrontendStatus.Unknown) {
            return;
        }

        if (isModerationFailed) {
            dispatch(setModerationFailedAlert({ isVisible: true, campaignId }));
            return;
        }

        if (isInsufficientBalance) {
            dispatch(setIsCampaignInsufficientBalanceAlertVisible(true));
            return;
        }

        if (frontendStatus === CampaignFrontendStatus.InactiveDueToEndDate) {
            dispatch(setInactiveDueToEndDateAlert({ isVisible: true, campaignId }));
            return;
        }

        if (
            [CampaignFrontendStatus.BudgetExceeded, CampaignFrontendStatus.DailyBudgetExceedStatus].includes(
                frontendStatus
            )
        ) {
            dispatch(setBudgetExceededAlert({ isVisible: true, campaignId }));
            return;
        }

        if (frontendStatus === CampaignFrontendStatus.Blocked) {
            abortRequest();

            setIsStatusToggleLoadingOn();
            void dispatch(updateCampaignStatus(campaignId, CampaignFrontendStatus.Active, abortSignal())).finally(
                setIsStatusToggleLoadingOff
            );
            return;
        }

        if (isActiveStatus) {
            dispatch(setStopCampaignAlert({ isVisible: true, campaignIds: new Set([campaignId]) }));
        }
    };

    return {
        isStatusToggleLoading,
        onToggleStatus,
    };
};
